import { useQuery } from '@tanstack/react-query';
import { PageHeader } from '../../components/ui';
import { formatMoney } from '../../lib/format';
import { links } from '../../lib/links';
import { useSession } from '../../lib/session';
import { KpiStrip, type Kpi } from './primitives';
import { QuickActions, StickyQuickActions, type QuickAction } from './QuickActions';
import { ExpiringWidget } from './widgets/ExpiringWidget';
import { LowStockWidget } from './widgets/LowStockWidget';
import { RecentMovementsWidget } from './widgets/RecentMovementsWidget';
import {
  expiringLotsQuery,
  lowStockQuery,
  onHoldQuery,
  receiptsTodayQuery,
  stockValuationQuery,
} from './queries';

const EXPIRING_DAYS = 30;

/**
 * The warehouse floor. The work is receiving, issuing and moving stock, so the
 * screen leads with what is running out or about to expire, and every low-stock
 * row can start its own receipt.
 */
export function WarehouseDashboard() {
  const { shows } = useSession();

  const valuation = useQuery(stockValuationQuery);
  const lowStock = useQuery(lowStockQuery);
  const expiring = useQuery(expiringLotsQuery(EXPIRING_DAYS));
  const hold = useQuery(onHoldQuery);
  const receipts = useQuery(receiptsTodayQuery());

  const lowCount = lowStock.data?.items.length;
  const expiringCount = (expiring.data?.items ?? []).filter(
    (lot) => lot.expiryDate !== null,
  ).length;
  const holdCount = hold.data?.items.length;
  const receiptsToday = receipts.data?.total;

  const kpis: Kpi[] = [
    {
      label: 'Stock value',
      value: valuation.data ? formatMoney(valuation.data.totals.quantityValue) : '—',
      to: shows('reports') ? '/reports' : undefined,
      tooltip:
        'Computed from the ledger, not a running total. Includes material that is on hold but still owned.',
    },
    {
      label: 'Low stock',
      value: lowCount === undefined ? '—' : String(lowCount),
      tone: lowCount ? 'warn' : 'good',
      hint: 'at or below reorder point',
    },
    {
      label: `Expiring ≤ ${EXPIRING_DAYS}d`,
      value: expiringCount === undefined ? '—' : String(expiringCount),
      tone: expiringCount ? 'warn' : 'default',
      hint: 'lots to use first',
    },
    {
      label: 'On hold',
      value: holdCount === undefined ? '—' : String(holdCount),
      hint: 'awaiting QC release',
      to: shows('quality') ? '/quality' : undefined,
    },
  ];

  const actions: QuickAction[] = [
    {
      label: 'Receive',
      to: links.inventoryReceive(),
      capability: 'inventory.manage',
      tone: 'primary',
    },
    { label: 'Issue', to: links.inventoryAction('issue'), capability: 'inventory.manage' },
    { label: 'Transfer', to: links.inventoryAction('transfer'), capability: 'inventory.manage' },
    { label: 'Adjust', to: links.inventoryAction('adjust'), capability: 'inventory.manage' },
    { label: 'Scrap', to: links.inventoryAction('scrap'), capability: 'inventory.manage' },
  ];

  return (
    <>
      <PageHeader
        eyebrow="Warehouse"
        title="Warehouse today"
        description="What is running out, what is about to expire, and what is stuck on hold. Stock on hand is never edited — every change is a movement in the ledger."
      />

      <KpiStrip items={kpis} />

      <div className="mt-4">
        <QuickActions actions={actions} />
      </div>

      <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-2">
        <LowStockWidget to={links.inventoryBalances()} />
        <ExpiringWidget days={EXPIRING_DAYS} to={links.inventoryBalances()} />
      </div>

      <div className="mt-4">
        <RecentMovementsWidget
          description={
            receiptsToday === undefined
              ? undefined
              : `${receiptsToday} receipt(s) recorded today. Every stock change is posted to the ledger as it happens.`
          }
          to={links.inventoryLedger()}
        />
      </div>

      <StickyQuickActions actions={actions} />
    </>
  );
}
